import { Controller } from "@hotwired/stimulus"

/**
 * Reading Progress Controller
 *
 * Two mounts:
 *
 *  - "bottom" (default; theme-appraisal "Immersive Editorial" chrome): a reader
 *    status bar fixed to the BOTTOM of the viewport while the chapter is on
 *    screen (the top overlaps the Book Reader header — QA round 1). It carries
 *    the mock's top-bar content: chapter label on the left (derived from the
 *    hero eyebrow + title), live quiz score + "N% read" on the right, and the
 *    gradient progress fill as its top edge. The quiz score arrives via the
 *    chapter-quiz controller's "chapter-quiz:score" event (QA round 2: the
 *    in-quiz chip scrolls away, so the score also lives here). Plus computed
 *    meta chips in the hero ("~N min read", "N sections", "N review
 *    questions") — derived from the chapter DOM, never authored.
 *
 *  - "topbar" (OpenStax reader): the fill rides the bottom edge of the fixed
 *    `.openstax-topbar` (the natural home on a layout that already has one),
 *    and the meta chips go in a `.reading-progress-meta` row right after the
 *    page title (`metaAfter` selector) together with a mini "N% read" bar.
 *    No bottom bar; the quiz score lives in the quiz's own chip.
 *
 * Usage: add data-controller="reading-progress" to the theme wrapper element
 * (composes with chapter-outline / key-terms / chapter-quiz on the same element).
 */
export default class extends Controller {
  static values = {
    // words-per-minute basis for the read-time chip
    wpm: { type: Number, default: 230 },
    mount: { type: String, default: "bottom" },
    metaAfter: { type: String, default: "" }
  }

  connect() {
    if (this.mountValue === "topbar") {
      this.#buildTopbarFill()
      this.#buildMetaRow()
    } else {
      this.#buildBar()
      this.#buildHeroOrbs()
      this.#buildHeroChips()
    }

    this.ticking = false
    this.scrollHandler = () => {
      if (!this.ticking) {
        this.ticking = true
        requestAnimationFrame(() => this.#update())
      }
    }
    window.addEventListener("scroll", this.scrollHandler, { passive: true })
    window.addEventListener("resize", this.scrollHandler, { passive: true })

    // live quiz score in the status bar (dispatched by chapter-quiz)
    this.scoreHandler = (e) => {
      if (!this.scoreEl) return
      const { score, answered, total, keyed } = e.detail
      // A quiz of worked problems only has nothing to score
      this.scoreEl.textContent = keyed === 0
        ? `${answered}/${total} solutions viewed`
        : `Score ${score} · ${answered}/${total} answered`
      this.scoreEl.classList.toggle("visible", answered > 0)
    }
    this.element.addEventListener("chapter-quiz:score", this.scoreHandler)

    this.#update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.scrollHandler)
    window.removeEventListener("resize", this.scrollHandler)
    this.element.removeEventListener("chapter-quiz:score", this.scoreHandler)
    this.bar?.remove()
    this.topbarFill?.remove()
  }

  // ---- topbar mount ------------------------------------------------------

  #buildTopbarFill() {
    const topbar = document.querySelector(".openstax-topbar")
    if (!topbar) return
    this.topbarFill = document.createElement("div")
    this.topbarFill.className = "reading-progress-fill"
    this.topbarFill.setAttribute("aria-hidden", "true")
    topbar.appendChild(this.topbarFill)
    this.fill = this.topbarFill
  }

  #buildMetaRow() {
    const title = this.metaAfterValue && this.element.querySelector(this.metaAfterValue)
    if (!title) return

    const row = document.createElement("div")
    row.className = "reading-progress-meta"
    // In a chapter-opener hero the read time is misleading next to the
    // chapter's section count (the intro page takes 6 min; the chapter
    // doesn't), so the hero shows only chapter-level chips.
    const chips = title.closest(".openstax-hero") ? this.#chips().filter(([, t]) => !t.endsWith("min read")) : this.#chips()
    chips.forEach(([glyph, text]) => row.appendChild(this.#chip(glyph, text)))
    if (row.childElementCount) title.insertAdjacentElement("afterend", row)

    // The "N% read" gauge rides the chapter outline, which is the sticky right
    // rail on wide reading pages — the one place that stays in view while
    // scrolling. In the chip row it scrolled away within a few lines (Jess,
    // 2026-09-09). The topbar fill is the indicator everywhere else.
    const outline = this.element.querySelector(".chapter-outline")
    if (!outline) return
    const pctWrap = document.createElement("div")
    pctWrap.className = "reading-progress-pct-wrap"
    const mini = document.createElement("span")
    mini.className = "reading-progress-mini"
    mini.setAttribute("aria-hidden", "true")
    this.miniFill = document.createElement("span")
    mini.appendChild(this.miniFill)
    this.pctEl = document.createElement("span")
    this.pctEl.className = "reading-progress-pct"
    pctWrap.appendChild(mini)
    pctWrap.appendChild(this.pctEl)
    outline.appendChild(pctWrap)
  }

  // Chips derived from the DOM: read time, sections (outline entries, or
  // section headings when the outline is not there), review questions.
  #chips() {
    const chips = []
    const words = (this.element.textContent.match(/\S+/g) || []).length
    chips.push(["◷", `~${Math.max(1, Math.round(words / this.wpmValue))} min read`])

    const outline = this.element.querySelectorAll(".chapter-outline > ul > li:not(.toc-sub):not(.toc-intro)").length
    // topbar mount (OpenStax): the h2 is the page title, so only an outline
    // counts — ours, or on a chapter Introduction OpenStax's own list of the
    // chapter's sections (.os-chapter-outline)
    const sections = this.mountValue === "topbar"
      ? outline || this.element.querySelectorAll(".os-chapter-outline li").length
      : outline || this.element.querySelectorAll("h2:not(.no-number)").length
    if (sections) chips.push(["▤", `${sections} section${sections === 1 ? "" : "s"}`])

    const review = this.#reviewQuestionCount()
    if (review) chips.push(["✎", `${review} review question${review === 1 ? "" : "s"}`])
    return chips
  }

  #chip(glyph, text) {
    const chip = document.createElement("span")
    chip.className = "hero-chip"
    const icon = document.createElement("span")
    icon.className = "hero-chip-icon"
    icon.setAttribute("aria-hidden", "true")
    icon.textContent = glyph
    chip.appendChild(icon)
    chip.appendChild(document.createTextNode(` ${text}`))
    return chip
  }

  // ---- bottom-bar mount (appraisal) -------------------------------------

  #buildBar() {
    this.bar = document.createElement("div")
    this.bar.className = "reading-progress"

    this.fill = document.createElement("div")
    this.fill.className = "reading-progress-fill"
    this.fill.setAttribute("aria-hidden", "true")
    this.bar.appendChild(this.fill)

    // left: gradient dot + chapter label, derived from the hero
    const left = document.createElement("div")
    left.className = "reading-progress-left"
    const dot = document.createElement("span")
    dot.className = "reading-progress-dot"
    dot.setAttribute("aria-hidden", "true")
    left.appendChild(dot)
    const h1 = this.element.querySelector(".module-hero h1")
    const eyebrow = h1?.getAttribute("data-eyebrow")
    if (eyebrow) {
      const label = document.createElement("span")
      label.className = "reading-progress-label"
      label.textContent = eyebrow
      left.appendChild(label)
    }
    const titleText = h1 && this.#heroTitleText(h1)
    if (titleText) {
      const title = document.createElement("span")
      title.className = "reading-progress-title"
      title.textContent = `/ ${titleText}`
      left.appendChild(title)
    }
    this.bar.appendChild(left)

    // right: quiz score (hidden until first answer) + % read
    const right = document.createElement("div")
    right.className = "reading-progress-right"
    this.scoreEl = document.createElement("span")
    this.scoreEl.className = "reading-progress-score"
    right.appendChild(this.scoreEl)
    this.pctEl = document.createElement("span")
    this.pctEl.className = "reading-progress-pct"
    right.appendChild(this.pctEl)
    this.bar.appendChild(right)

    document.body.appendChild(this.bar)
  }

  #heroTitleText(h1) {
    const clone = h1.cloneNode(true)
    clone.querySelector(".chapter-subtitle")?.remove()
    return clone.textContent.trim()
  }

  #buildHeroOrbs() {
    const hero = this.element.querySelector(".module-hero")
    if (!hero) return
    // append (not prepend): the h1 must stay :first-child of the hero, or the
    // theme's secondary-h1 de-emphasis rule swallows the title
    ;["hero-orb hero-orb-a", "hero-orb hero-orb-b"].forEach((className) => {
      const orb = document.createElement("div")
      orb.className = className
      orb.setAttribute("aria-hidden", "true")
      hero.append(orb)
    })
  }

  #buildHeroChips() {
    const hero = this.element.querySelector(".module-hero h1")
    if (!hero) return

    const row = document.createElement("div")
    row.className = "hero-meta"
    this.#chips().forEach(([glyph, text]) => row.appendChild(this.#chip(glyph, text)))
    hero.parentNode.appendChild(row)
  }

  #reviewQuestionCount() {
    const heading = Array.from(this.element.querySelectorAll("h2")).find(
      (h) => h.textContent.trim().replace(/^[\d.\s]+/, "") === "Review Exercises"
    )
    if (heading) {
      let el = heading.nextElementSibling
      while (el && el.tagName !== "OL") el = el.nextElementSibling
      return el ? el.children.length : 0
    }
    // OpenStax Section Quiz page: one exercise block per question
    return this.element.querySelectorAll('[data-type="exercise"]').length
  }

  // ---- shared ------------------------------------------------------------

  #update() {
    this.ticking = false
    const rect = this.element.getBoundingClientRect()
    const vh = window.innerHeight

    // Only show the bottom bar while the chapter is on screen
    const visible = rect.top < vh && rect.bottom > 0
    this.bar?.classList.toggle("visible", visible)
    if (this.bar && !visible) return

    const total = rect.height - vh
    const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1
    const pct = `${Math.round(progress * 100)}%`
    if (this.fill) this.fill.style.width = pct
    if (this.miniFill) this.miniFill.style.width = pct
    if (this.pctEl) this.pctEl.textContent = `${pct} read`
  }
}
